import React, { useState, useCallback, useRef ,useEffect} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import { tableClasses } from './constants';

const extractTextFromJSX = node => {
  try {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return node.toString();
    if (Array.isArray(node)) {
      return node.map(extractTextFromJSX).join(' ');
    }
    if (React.isValidElement(node)) {
      return extractTextFromJSX(node.props?.children);
    }
    return '';
  } catch (err) {
    console.error('Error extracting text from JSX:', err);
    return '';
  }
};

const defState = {
  page: 0,
  rowsPerPage: 15,
};

export default function DisplayTable(props) {
  const {
    entity,
    emptyTableMsg = undefined,
    onClick = () => console.log('Create entity'),
    tableEntries = [],
    tableTitles = [],
    toShowAddBtn = true,
    otherEntryCellSxProps,
    animationClass = '',
    currDarkThemeClass = '',
    enablePagination = true,
    rowsPerPageOptions = [10, 15, 30, 100, 500],
    rowsPerPage = 15,
    showSearch = false,
    onSearch,
    useBackendSearch = false,
    enableMultiTermSearch = false,
    showFilterChips = false,
    filterOptions = [],
    activeFilter = null,
    onFilterChange = () => {},
    initialSearchTerms = [], // ADD THIS NEW PROP
    ...otherProps
  } = props;

  const [values, setValues] = useState({ ...defState, rowsPerPage });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerms, setSearchTerms] = useState([]);
  const [validateFilter, setValidateFilter] = useState(null);
  const searchInputRef = useRef(null);

  React.useEffect(() => {
  if (initialSearchTerms && initialSearchTerms.length > 0) {
    const newTermsString = initialSearchTerms.join(',');
    const currentTermsString = searchTerms.join(',');
    
    if (newTermsString !== currentTermsString) {
      setSearchTerms(initialSearchTerms);
      setSearchQuery(initialSearchTerms.join(' '));
      setSearchInput('');
    }
  }
}, [initialSearchTerms, enableMultiTermSearch]); // Add enableMultiTermSearch to deps

  // Extract styles to reduce inline styling in return statement
  const searchContainerSx = {
    padding: '5px',
    backgroundColor: currDarkThemeClass === 'dark-theme' ? '#242523' : '#ffffff',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  };

  const searchBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  };

  const searchControlsSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    minHeight: '40px',
  };

  const filterChipsContainerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  };

  const filterChipsBoxSx = {
    display: 'flex',
    gap: 1,
  };

const searchFieldSx = {
  width: searchTerms.length > 0 ? Math.max(300, 300 + searchTerms.length * 90) : 300,
  transition: 'width 0.3s ease',
  
  '& .MuiInputLabel-root': {
    color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#666 !important',
    '&.Mui-focused': {
      color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#666 !important',
    },
    '&.MuiInputLabel-shrink': {
      color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#666 !important',
    },
  },
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: currDarkThemeClass === 'dark-theme' ? '#333 !important' : '#fff !important',
    color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#333 !important',
    
    '& .MuiInputBase-input': {
      minWidth: '100px',
      caretColor: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#000 !important',
      color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#333 !important',
      backgroundColor: 'transparent !important',
      '&:focus': {
        caretColor: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#000 !important',
        color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#333 !important',
        backgroundColor: 'transparent !important',
      },
      '&::placeholder': {
        color: currDarkThemeClass === 'dark-theme' ? '#aaa !important' : '#999 !important',
        opacity: '1 !important',
      },
    },
    
    '& fieldset': {
      borderColor: currDarkThemeClass === 'dark-theme' ? '#555 !important' : '#ddd !important',
    },
    '&:hover fieldset': {
      borderColor: currDarkThemeClass === 'dark-theme' ? '#777 !important' : '#bbb !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: currDarkThemeClass === 'dark-theme' ? '#999 !important' : '#999 !important',
      borderWidth: '1px !important', // Prevent thick border
    },
    
    '&.Mui-focused': {
      backgroundColor: currDarkThemeClass === 'dark-theme' ? '#333 !important' : '#fff !important',
      
      '& .MuiInputBase-input': {
        backgroundColor: 'transparent !important',
        color: currDarkThemeClass === 'dark-theme' ? '#fff !important' : '#333 !important',
      },
    },
    
    '&.MuiOutlinedInput-root.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: currDarkThemeClass === 'dark-theme' ? '#999 !important' : '#999 !important',
      },
    },
  },

  '&.MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: currDarkThemeClass === 'dark-theme' ? '#999 !important' : '#999 !important',
        },
      },
    },
  },
};

  const searchTermsContainerSx = {
    display: 'flex',
    gap: 0.5,
    marginRight: 1,
    marginBottom: 0.5,
    marginTop: 0.5,
  };

  const searchTermChipSx = {
    height: '24px',
    backgroundColor: currDarkThemeClass === 'dark-theme' ? '#4a4a4a' : '#f5f5f5',
    color: 'white',
    fontSize: '15px',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '4px',
      fontSize: '15px',
      fontWeight: '500',
    },
    '& .MuiChip-deleteIcon': {
      fontSize: '16px',
      color: 'rgba(255,255,255,0.8)',
      '&:hover': {
        color: 'white',
      },
    },
  };

  const clearAllButtonSx = {
    minWidth: 'auto',
    height: '24px',
    fontSize: '15px',
    px: 0.5,
    color: currDarkThemeClass === 'dark-theme' ? '#ccc' : '#666',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      color: currDarkThemeClass === 'dark-theme' ? '#fff' : '#333',
    },
  };

  const searchIconButtonSx = {
    color: currDarkThemeClass === 'dark-theme' ? '#ccc' : '#666',
  };

  const getFilterChipSx = (filter, isActive) => ({
    height: '28px',
    backgroundColor: isActive ? filter.color || '#B1D23A' : 'transparent',
    color: isActive ? filter.textColor || '#000' : currDarkThemeClass === 'dark-theme' ? '#fff' : '#000',
    borderColor: filter.color || '#B1D23A',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: isActive ? filter.hoverColor || '#9BC234' : `${filter.color || '#B1D23A'}20`,
      transform: 'scale(1.02)',
    },
    '& .MuiChip-label': {
      paddingLeft: isActive ? '4px' : '12px',
      paddingRight: '12px',
      fontSize: '12px',
      fontWeight: '500',
    },
    '& .MuiChip-icon': {
      marginLeft: '8px',
      marginRight: '0px',
      color: isActive ? filter.textColor || '#000' : 'inherit',
    },
  });

  const handleSetValues = useCallback((prop, value) => {
    setValues(state => ({ ...state, [prop]: value }));
  }, []);

  const handleChangePage = useCallback(
    (event, newPage) => {
      if (props.handleChangePage) {
        props.handleChangePage(event, newPage);
      } else {
        handleSetValues('page', +newPage);
      }
    },
    [props.handleChangePage]
  );

  const handleChangeRowsPerPage = useCallback(
    event => {
      if (props.handleChangeRowsPerPage) {
        props.handleChangeRowsPerPage(event);
      } else {
        const value = event.target.value;
        handleSetValues('page', 0);
        handleSetValues('rowsPerPage', +value);
      }
    },
    [props.handleChangeRowsPerPage]
  );

  const handleSearchInputChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchInput(value);

      if (!useBackendSearch && !enableMultiTermSearch) {
        setSearchQuery(value);
      }
    },
    [useBackendSearch, enableMultiTermSearch]
  );

  const parseSearchTerms = useCallback(
    input => {
      if (!enableMultiTermSearch) {
        return [input.trim()];
      }

      const terms = input
        .trim()
        .split(/\s+/)
        .filter(term => term.length > 0);
      return terms;
    },
    [enableMultiTermSearch]
  );

const executeSearch = useCallback(async () => {
  const trimmedInput = searchInput.trim();
  if (!trimmedInput) return;

  if (useBackendSearch && onSearch) {
    setIsSearching(true);

    const newTerms = [...searchTerms, trimmedInput];
    const combinedQuery = newTerms.join(' ');

    setSearchTerms(newTerms);
    setSearchQuery(combinedQuery);
    setSearchInput('');

    try {
      await onSearch(combinedQuery, newTerms);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);

      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  } else if (!useBackendSearch && enableMultiTermSearch) {
    const newTerms = [...searchTerms, trimmedInput];
    const combinedQuery = newTerms.join(' ');

    setSearchTerms(newTerms);
    setSearchQuery(combinedQuery);
    setSearchInput('');
    
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  }
}, [useBackendSearch, onSearch, searchInput, searchTerms, enableMultiTermSearch]);
  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        executeSearch();
      }
    },
    [executeSearch]
  );

  const handleSearchButtonClick = useCallback(() => {
    executeSearch();
  }, [executeSearch]);

  const handleRemoveSearchTerm = useCallback(
    termToRemove => {
      const newTerms = searchTerms.filter(term => term !== termToRemove);
      setSearchTerms(newTerms);

      if (searchInput.trim() === termToRemove) {
        setSearchInput('');
      }

      const newSearchQuery = newTerms.join(' ');
      setSearchQuery(newSearchQuery);

      if (useBackendSearch && onSearch) {
        onSearch(newSearchQuery, newTerms);
      }
    },
    [searchTerms, searchInput, useBackendSearch, onSearch]
  );

  const handleClearAllTerms = useCallback(() => {
    setSearchTerms([]);
    setSearchQuery('');
    if (useBackendSearch && onSearch) {
      onSearch('', []);
    }
  }, [useBackendSearch, onSearch]);

  const handleClearInput = useCallback(() => {
    setSearchInput('');
  }, []);
  
  const titlesLen = tableTitles.length;

  const displayTableBaseProps = {
    ...otherProps.baseProps,
  };

  const displayTableContainerProps = {
    className: `${tableClasses?.displayTable?.tableContainer} ${currDarkThemeClass} ${animationClass}`,
    component: Paper,
    variant: 'none',
    ...otherProps?.tableContainerProps,
  };

  const displayTableProps = {
    className: `${tableClasses?.displayTable?.table} ${currDarkThemeClass}`,
    stickyHeader: true,
  };

  const displayTableRowProps = {
    className: `${tableClasses?.displayTable?.tableRow} ${currDarkThemeClass}`,
    ...otherProps?.tableRowProps,
  };

  const displayTableTitleCellProps = i => ({
    className: `${tableClasses?.displayTable?.titleCell} ${currDarkThemeClass}`,
    sx: {
      pl: i === 0 || i === titlesLen - 1 ? '39px' : '20px',
      pr: i === titlesLen - 1 ? '39px' : 'auto',
    },
    variant: 'head',
    ...otherProps?.tableTitleProps,
  });

  const displayTableEntryCellProps = idx => ({
    className: `${tableClasses?.displayTable?.entryCell} ${currDarkThemeClass}`,
    ...(idx === 0 && {
      component: 'th',
      scope: 'row',
    }),
    sx: {
      pl: idx === 0 || idx === titlesLen - 1 ? '39px' : '20px',
      pr: idx === titlesLen - 1 ? '39px' : 'auto',
      ...otherEntryCellSxProps?.(idx),
    },
    variant: 'body',
    ...otherProps?.tableEntryCellProps,
  });

  const tableTitlesList = tableTitles.map((e, i) => (
    <TableCell key={i} {...displayTableTitleCellProps(i)}>
      {e.title}
    </TableCell>
  ));

  const filteredEntries = useBackendSearch
    ? tableEntries
    : tableEntries.filter(row => {
        if (!enableMultiTermSearch) {
          return row.some(cell => {
            if (typeof cell === 'string' || typeof cell === 'number') {
              return String(cell).toLowerCase().includes(searchQuery.toLowerCase());
            }
            if (React.isValidElement(cell)) {
              const textContent = extractTextFromJSX(cell);
              return textContent.toLowerCase().includes(searchQuery.toLowerCase());
            }
            return false;
          });
        } else {
          const terms = parseSearchTerms(searchQuery);
          return terms.every(term =>
            row.some(cell => {
              if (typeof cell === 'string' || typeof cell === 'number') {
                return String(cell).toLowerCase().includes(term.toLowerCase());
              }
              if (React.isValidElement(cell)) {
                const textContent = extractTextFromJSX(cell);
                return textContent.toLowerCase().includes(term.toLowerCase());
              }
              return false;
            })
          );
        }
      });

  const currentPage = typeof props.page === 'number' ? props.page : values.page;
  const currentRowsPerPage = typeof props.rowsPerPage === 'number' ? props.rowsPerPage : values.rowsPerPage;

  const slicedTableEntries =
    props.page != null || useBackendSearch
      ? filteredEntries
      : filteredEntries.slice(currentPage * currentRowsPerPage, currentPage * currentRowsPerPage + currentRowsPerPage);

  const tableEntriesList = slicedTableEntries.map((e, i) => (
    <TableRow key={`row-${i}`} {...displayTableRowProps}>
      {e.map((el, idx) => (
        <TableCell key={`cell-${idx}`} {...displayTableEntryCellProps(idx)}>
          {el ?? ''}
        </TableCell>
      ))}
    </TableRow>
  ));

  const noEntityProps = {
    className: `${tableClasses?.displayTable?.noEntityTableCell} ${currDarkThemeClass}`,
    colSpan: '100%',
  };

  const displayTableFooterPaperProps = {
    className: `${tableClasses?.displayTable?.tableFooterPaper} ${currDarkThemeClass} ${animationClass}`,
    square: true,
    variant: 'none',
  };

  const displayTableAddBtnProps = {
    className: `${tableClasses?.displayTable?.addBtn} ${currDarkThemeClass}`,
    onClick,
  };

  const displayTablePaginationProps = {
    className: `${tableClasses?.displayTable?.pagination} ${currDarkThemeClass}`,
    component: 'div',
    count: typeof props.count === 'number' ? props.count : filteredEntries.length,
    page: currentPage,
    onPageChange: props.handleChangePage ?? ((e, newPage) => handleSetValues('page', newPage)),
    rowsPerPage: currentRowsPerPage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions,
  };

  return (
    <Box {...displayTableBaseProps}>
      {showSearch && (
        <Box sx={searchContainerSx}>
          <Box sx={searchBoxSx}>
            <Box sx={{ minHeight: '40px' }} />
            <Box sx={searchControlsSx}>
              {showFilterChips && filterOptions.length > 0 && (
                <Box sx={filterChipsContainerSx}>
                  <Box sx={filterChipsBoxSx}>
                    {filterOptions.map(filter => (
                      <Chip
                        key={filter.value}
                        label={filter.label}
                        onClick={() => onFilterChange(filter.value)}
                        variant={activeFilter === filter.value ? 'filled' : 'outlined'}
                        size="small"
                        icon={activeFilter === filter.value ? <CheckIcon sx={{ fontSize: '16px !important' }} /> : undefined}
                        sx={getFilterChipSx(filter, activeFilter === filter.value)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              <TextField
                label="Search"
                inputRef={searchInputRef}
                variant="outlined"
                size="small"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleKeyPress}
                sx={searchFieldSx}
                placeholder="Search Here..."
                disabled={isSearching}
                InputProps={{
                  startAdornment:
                    enableMultiTermSearch && searchTerms.length > 0 ? (
                      <Box sx={searchTermsContainerSx}>
                        {searchTerms.map((term, index) => (
                          <Chip
                            key={`${term}-${index}`}
                            label={term}
                            onDelete={() => handleRemoveSearchTerm(term)}
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={searchTermChipSx}
                          />
                        ))}
                        {searchTerms.length > 1 && (
                          <Button
                            size="small"
                            onClick={handleClearAllTerms}
                            variant="text"
                            sx={clearAllButtonSx}
                          >
                            Clear
                          </Button>
                        )}
                      </Box>
                    ) : null,
                  endAdornment:
                    useBackendSearch || enableMultiTermSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleSearchButtonClick}
                          edge="end"
                          size="small"
                          title="Search"
                          disabled={isSearching}
                          sx={searchIconButtonSx}
                        >
                          {isSearching ? <CircularProgress size={18} /> : <SearchIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <TableContainer {...displayTableContainerProps}>
        <Table {...displayTableProps}>
          <TableHead>
            <TableRow>{tableTitlesList}</TableRow>
          </TableHead>
          <TableBody>
            {tableEntriesList.length === 0 ? (
              <TableRow>
                <TableCell {...noEntityProps}>{emptyTableMsg ?? `No ${entity} Added`}</TableCell>
              </TableRow>
            ) : (
              tableEntriesList
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {enablePagination && <TablePagination {...displayTablePaginationProps} />}
      {toShowAddBtn && (
        <Paper {...displayTableFooterPaperProps}>
          <Button {...displayTableAddBtnProps}>Add {entity}</Button>
        </Paper>
      )}
    </Box>
  );
}